/**
 * Shared types for the StokApp web demo.
 *
 * These mirror the Swift models in the Mac app closely enough that the rule engines can be
 * compared line-for-line, but only carry the fields the demo actually needs — this is a
 * demonstration of how the rules work, not a reimplementation of the app.
 *
 * All ratios here are FRACTIONS, never percentages: 0.052 means 5.2%. This matches
 * StrategyConfig's units in the Swift app and is the single most common source of bugs in this
 * domain (see the yield-scale incident documented in EntryScreen.swift), so it is enforced by
 * naming every such field `...Fraction` wherever ambiguity is possible.
 */

/** One daily bar of synthetic price history. */
export interface Bar {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  close: number;
  high: number;
  low: number;
}

/** A demo instrument. Entirely fictional — see data/README for why. */
export interface DemoStock {
  ticker: string;
  name: string;
  /** Fictional market id, e.g. "NRX" or "TLX". */
  market: string;
  currency: string;
  price: number;
  previousClose: number;
  /** Trailing annual dividend per share, in `currency`. Null for non-payers. */
  annualDividendPerShare: number | null;
  fiftyTwoWeekHigh: number;
  fiftyTwoWeekLow: number;
  /** 200-day simple moving average, precomputed from `history`. */
  sma200: number | null;
  history: Bar[];
  sector: string;
}

/** A holding in the demo portfolio. */
export interface DemoPosition {
  ticker: string;
  shares: number;
  /** Average cost basis per share, in the stock's own currency. */
  averageCost: number;
}

/**
 * The user-tunable thresholds. In the Mac app these live in StrategyConfig and are edited in
 * Settings; in the demo they're driven by sliders, which is the whole point — watching badges
 * change as you move them is what actually communicates how the app works.
 */
export interface Thresholds {
  /** Max distance above the 52-week low to count as "near the low". 0.15 = within 15%. */
  nearLowFraction: number;
  /** Minimum dividend yield to qualify. 0.03 = 3%. */
  minYieldFraction: number;
  /** Yield sanity ceiling. Above this, a hit is flagged rather than clean. 0.10 = 10%. */
  maxYieldFraction: number;
  entryScreenEnabled: boolean;
  trendFilterEnabled: boolean;
}

export const DEFAULT_THRESHOLDS: Thresholds = {
  nearLowFraction: 0.15,
  minYieldFraction: 0.03,
  maxYieldFraction: 0.10,
  entryScreenEnabled: true,
  trendFilterEnabled: true,
};

/** Mirrors MarketStance in the Swift app. */
export type Stance = 'buy' | 'watch' | 'hold' | 'sell';

/** Mirrors EntryScreen.Verdict. */
export type Verdict = 'qualifies' | 'highYieldFlagged' | 'doesNotQualify';

export interface StanceResult {
  stance: Stance;
  /** Plain-English reason naming the rule that fired and the numbers behind it. */
  rationale: string;
}
